import { useState, useRef, useEffect } from "react";
import { Button, Spin } from "antd";
import { CheckCircleOutlined, RobotOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DECISION_TREE, Category, Issue } from "./decisionTree";
import {
  askAI,
  escalateToTicket,
  AIMessage,
  TicketResponse,
} from "../helpCenter.service";

type Step =
  | "category"
  | "issue"
  | "ai_thinking"
  | "ai_answer"
  | "escalate"
  | "done";

type Props = {
  projectId: string;
  onEscalated: (ticket: TicketResponse) => void;
};

const TechnicalSupportChat = ({ projectId, onEscalated }: Props) => {
  const { t } = useTranslation("helpCenter");
  const categoryLabel = (cat: Category) =>
    t(`decisionTree.${cat.id}.label`, cat.label);
  const issueLabel = (categoryId: string, issue: Issue) =>
    t(`decisionTree.${categoryId}.issues.${issue.id}.label`, issue.label);
  const [step, setStep] = useState<Step>("category");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [aiMessages, setAiMessages] = useState<AIMessage[]>([]);
  const [aiAnswer, setAiAnswer] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [ticketDraft, setTicketDraft] = useState<{
    subject: string;
    description: string;
  } | null>(null);
  const [escalating, setEscalating] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages, step]);

  const handleCategory = (cat: Category) => {
    setSelectedCategory(cat);
    if (cat.id === "other" || cat.issues.length === 0) {
      handleEscalate([], cat.label, "");
    } else {
      setStep("issue");
    }
  };

  const handleIssue = async (issue: Issue) => {
    setSelectedIssue(issue);

    if (issue.id.startsWith("other")) {
      handleEscalate([], selectedCategory?.label ?? "", issue.label);
      return;
    }

    // Send to AI
    const userMsg: AIMessage = {
      role: "user",
      content: `Category: ${selectedCategory?.label}. Issue: ${issue.label}`,
    };
    const updated = [...aiMessages, userMsg];
    setAiMessages(updated);
    setStep("ai_thinking");

    try {
      const res = await askAI({
        messages: updated,
        category: selectedCategory?.label,
        issue: issue.label,
        conversation_id: conversationId,
      });

      setConversationId(res.conversation_id);

      const assistantMsg: AIMessage = {
        role: "assistant",
        content: res.answer || t("technicalSupport.fallbackAnswer"),
      };
      setAiMessages((prev) => [...prev, assistantMsg]);

      if (res.action === "escalate") {
        setTicketDraft(res.ticket_draft ?? null);
        setStep("escalate");
      } else {
        setAiAnswer(res.answer);
        setStep("ai_answer");
      }
    } catch {
      setTicketDraft(null);
      setStep("escalate");
    }
  };

  const handleEscalate = async (
    msgs: AIMessage[],
    category: string,
    issue: string,
  ) => {
    setEscalating(true);
    try {
      const ticket = await escalateToTicket({
        ...(projectId ? { project_id: projectId } : {}),
        conversation_id: conversationId,
        category,
        issue,
        subject: ticketDraft?.subject,
        description: ticketDraft?.description,
        messages: msgs.length > 0 ? msgs : aiMessages,
      });
      onEscalated(ticket);
    } catch {
      // handled by interceptor
    } finally {
      setEscalating(false);
    }
  };

  const handleReset = () => {
    setStep("category");
    setSelectedCategory(null);
    setSelectedIssue(null);
    setAiMessages([]);
    setAiAnswer("");
    setTicketDraft(null);
    setConversationId(undefined);
  };

  if (loadingHistory) {
    return (
      <div className="tech-support-chat">
        <div className="tech-chat-header">
          <div className="tech-chat-header-icon">
            <RobotOutlined />
          </div>
          <div>
            <strong>{t("technicalSupport.header")}</strong>
            <span className="tech-chat-online">● {t("technicalSupport.online")}</span>
          </div>
        </div>
        <div
          className="tech-chat-body"
          style={{ justifyContent: "center", alignItems: "center" }}
        >
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className="tech-support-chat">
      {/* Header */}
      <div className="tech-chat-header">
        <div className="tech-chat-header-icon">
          <RobotOutlined />
        </div>
        <div>
          <strong>{t("technicalSupport.header")}</strong>
          <span className="tech-chat-online">● {t("technicalSupport.online")}</span>
        </div>
      </div>

      {/* Body */}
      <div className="tech-chat-body">
        {/* Welcome */}
        <div className="tech-bot-message">
          <div className="tech-avatar-ai">
            <RobotOutlined />
          </div>
          <div className="tech-bubble tech-bubble-ai">
            {t("technicalSupport.welcome")}
          </div>
        </div>

        {/* Step 1 — Category */}
        {step === "category" && (
          <div className="tech-button-group">
            {DECISION_TREE.map((cat) => (
              <Button key={cat.id} onClick={() => handleCategory(cat)}>
                {categoryLabel(cat)}
              </Button>
            ))}
          </div>
        )}

        {/* Step 2 — Issue */}
        {selectedCategory && step !== "category" && (
          <>
            <div className="tech-user-message">
              <div className="tech-bubble tech-bubble-user">
                {categoryLabel(selectedCategory)}
              </div>
            </div>
            <div className="tech-bot-message">
              <div className="tech-avatar-ai">
                <RobotOutlined />
              </div>
              <div className="tech-bubble tech-bubble-ai">
                {t("technicalSupport.issueQuestion", {
                  category: categoryLabel(selectedCategory),
                })}
              </div>
            </div>
            {step === "issue" && (
              <div className="tech-button-group">
                {selectedCategory.issues.map((issue) => (
                  <Button key={issue.id} onClick={() => handleIssue(issue)}>
                    {issueLabel(selectedCategory.id, issue)}
                  </Button>
                ))}
              </div>
            )}
          </>
        )}

        {/* AI thinking */}
        {step === "ai_thinking" && (
          <div className="tech-bot-message">
            <div className="tech-avatar-ai">
              <RobotOutlined />
            </div>
            <div className="tech-bubble tech-bubble-ai tech-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {/* AI answer */}
        {selectedIssue &&
          (step === "ai_answer" || step === "escalate" || step === "done") && (
            <>
              <div className="tech-user-message">
                <div className="tech-bubble tech-bubble-user">
                  {issueLabel(selectedCategory!.id, selectedIssue)}
                </div>
              </div>
              {aiAnswer && (
                <div className="tech-bot-message">
                  <div className="tech-avatar-ai">
                    <RobotOutlined />
                  </div>
                  <div className="tech-bubble tech-bubble-ai">{aiAnswer}</div>
                </div>
              )}
            </>
          )}

        {/* Did this help? */}
        {step === "ai_answer" && (
          <>
            <div className="tech-bot-message">
              <div className="tech-avatar-ai">
                <RobotOutlined />
              </div>
              <div className="tech-bubble tech-bubble-ai">
                {t("technicalSupport.solvedQuestion")}
              </div>
            </div>
            <div className="tech-button-group">
              <Button
                icon={<CheckCircleOutlined />}
                className="tech-btn-success"
                onClick={() => setStep("done")}
              >
                {t("technicalSupport.solvedYes")}
              </Button>
              <Button onClick={() => setStep("escalate")}>
                {t("technicalSupport.solvedNo")}
              </Button>
            </div>
          </>
        )}

        {/* Escalate */}
        {step === "escalate" && (
          <>
            <div className="tech-bot-message">
              <div className="tech-avatar-ai">
                <RobotOutlined />
              </div>
              <div className="tech-bubble tech-bubble-ai">
                {t("technicalSupport.escalateMessage")}
              </div>
            </div>
            <div className="tech-button-group">
              <Button
                type="primary"
                loading={escalating}
                onClick={() =>
                  handleEscalate(
                    aiMessages,
                    selectedCategory?.label ?? "",
                    selectedIssue?.label ?? "",
                  )
                }
              >
                {t("technicalSupport.escalateButton")}
              </Button>
            </div>
          </>
        )}

        {/* Done */}
        {step === "done" && (
          <div className="tech-bot-message">
            <div className="tech-avatar-ai">
              <RobotOutlined />
            </div>
            <div className="tech-bubble tech-bubble-ai">
              {t("technicalSupport.doneMessage")}
              <br />
              <br />
              <button className="tech-restart-link" onClick={handleReset}>
                {t("technicalSupport.startOver")}
              </button>
            </div>
          </div>
        )}

        <div ref={chatBottomRef} />
      </div>
    </div>
  );
};

export default TechnicalSupportChat;
